import type { LinkableProduct, StockItemOption } from '@agos/api-client/model'
import { T, formatMoney, tt } from '@agos/shared'
import { cn } from '@agos/ui'
import { Search } from 'lucide-react'
import { useMemo, useState } from 'react'

/**
 * The searchable picker on each ingredient row, ported from legacy.
 *
 * It offers two different things in one list: a **stock item** (flour, cheese)
 * and a **product** (a whole pizza used inside a party box). Which one is
 * chosen changes what the recipe consumes, so the two are kept visibly apart —
 * separate groups in the dropdown, and an S or P badge on the row.
 */

const UNSEARCHED_STOCK_LIMIT = 30
const UNSEARCHED_PRODUCT_LIMIT = 15
/** Long enough for a click to land before the blur closes the list. */
const BLUR_CLOSE_DELAY_MS = 150

interface StockItemPickerProps {
  stockItems: StockItemOption[]
  products: LinkableProduct[]
  stockItemId: number | null
  productId: number | null
  displayName: string
  onSelect: (stockItem: StockItemOption | null, product: LinkableProduct | null) => void
}

export function StockItemPicker({
  stockItems,
  products,
  stockItemId,
  productId,
  displayName,
  onSelect,
}: StockItemPickerProps) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const matchingStockItems = useMemo(() => {
    if (!query) return stockItems.slice(0, UNSEARCHED_STOCK_LIMIT)
    const needle = query.toLowerCase()
    return stockItems.filter(
      (item) =>
        item.name?.toLowerCase().includes(needle) || item.category?.toLowerCase().includes(needle),
    )
  }, [stockItems, query])

  const matchingProducts = useMemo(() => {
    if (!query) return products.slice(0, UNSEARCHED_PRODUCT_LIMIT)
    const needle = query.toLowerCase()
    return products.filter((product) => product.name?.toLowerCase().includes(needle))
  }, [products, query])

  const hasSelection = stockItemId !== null || productId !== null
  const selectedName =
    (stockItemId !== null
      ? stockItems.find((item) => item.id === stockItemId)?.name
      : productId !== null
        ? products.find((product) => product.id === productId)?.name
        : undefined) ?? displayName

  const choose = (stockItem: StockItemOption | null, product: LinkableProduct | null) => {
    onSelect(stockItem, product)
    setQuery('')
    setIsOpen(false)
  }

  return (
    <div className="relative flex-1">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={isOpen ? query : selectedName}
          onChange={(event) => {
            setQuery(event.target.value)
            if (!isOpen) setIsOpen(true)
          }}
          onFocus={() => {
            setIsOpen(true)
            setQuery('')
          }}
          onBlur={() => setTimeout(() => setIsOpen(false), BLUR_CLOSE_DELAY_MS)}
          placeholder={tt('Hľadať položku alebo produkt...')}
          className={cn(
            'w-full pl-7 pr-3 py-2 rounded-xl bg-background border text-xs outline-none focus:ring-2 focus:ring-primary/20 transition-all',
            hasSelection ? 'border-primary/40 text-foreground' : 'border-border text-muted-foreground',
          )}
        />
      </div>

      {isOpen && (matchingStockItems.length > 0 || matchingProducts.length > 0) && (
        <div className="absolute top-full left-0 mt-1 z-50 w-full bg-popover border border-border rounded-xl shadow-lg max-h-56 overflow-y-auto">
          {hasSelection && (
            <button
              type="button"
              // mousedown, not click: blur fires first and would close the list.
              onMouseDown={(event) => {
                event.preventDefault()
                choose(null, null)
              }}
              className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <T>Zrušiť výber</T>
            </button>
          )}

          {matchingStockItems.length > 0 && (
            <>
              <div className="px-3 py-1 text-[10px] font-bold text-muted-foreground uppercase tracking-wider bg-muted/30">
                <T>Skladové položky</T>
              </div>
              {matchingStockItems.map((item) => (
                <button
                  key={`stock-${item.id}`}
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault()
                    choose(item, null)
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors flex justify-between items-center',
                    item.id === stockItemId && 'bg-primary/10 text-primary font-semibold',
                  )}
                >
                  <span>
                    <T>{item.name}</T>
                  </span>
                  <span className="text-muted-foreground text-[10px]">
                    {item.category} · {item.unit}
                  </span>
                </button>
              ))}
            </>
          )}

          {matchingProducts.length > 0 && (
            <>
              <div className="px-3 py-1 text-[10px] font-bold text-muted-foreground uppercase tracking-wider bg-muted/30">
                <T>Produkty</T>
              </div>
              {matchingProducts.map((product) => (
                <button
                  key={`product-${product.id}`}
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault()
                    choose(null, product)
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors flex justify-between items-center',
                    product.id === productId && 'bg-violet-500/10 text-violet-500 font-semibold',
                  )}
                >
                  <span>
                    <T>{product.name}</T>
                  </span>
                  <span className="text-violet-400 text-[10px]">{formatMoney(product.price)}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}
